import SidePanel from '@monofront/design-system/organisms/SidePanel';
import {
  AddressFormPanel,
  PickUpNeededPanel,
  SelectedShippingMethodInformationPanel,
  ShippingMethodSelectionPanel,
} from '@features/DeliveryBar/Panels';
import {
  STATES_ENUM,
  TRANSITIONS_STATES,
  UI_STATES,
} from '@monofront/business/stateMachines/deliveryBar/states';
import {
  DeliveryBarMachineContext,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import { machineValueSelector } from '@monofront/business/stateMachines/deliveryBar/selectors';
import { get, isString } from 'radash';
import { useSelector } from '@xstate/react';
import { useDeliveryBar } from '@context/DeliveryBarContext';
import { ABTEST_DELIVERY_BAR } from '@monofront/business/constants';

type Panel = React.FC<{ closePanel: () => void }>;

const PlaceholderPanel: Panel = () => null;

const panels: {
  [key: string]: Panel | { [key: string]: Panel };
} = {
  [UI_STATES.ADDRESS_SELECTOR]: AddressFormPanel,
  [UI_STATES.SHIPPING_METHOD_SELECTOR]: {
    [UI_STATES.SHIPPING_METHOD_NOT_SELECTED]: ShippingMethodSelectionPanel,
    [UI_STATES.NEED_PICKUP_POINT_SELECTION]: PickUpNeededPanel,
  },
  [UI_STATES.SUMMARY]: SelectedShippingMethodInformationPanel,
  [TRANSITIONS_STATES.TRANSITION_INITIAL_STATE_RESOLVER]: PlaceholderPanel,
  [TRANSITIONS_STATES[
    STATES_ENUM.TRANSITION_ASYNC_FETCH_ELIGIBLE_SHIPPING_METHODS
  ]]: PlaceholderPanel,
  [TRANSITIONS_STATES[STATES_ENUM.TRANSITION_ASYNC_SAVE_CUSTOMER_DATA]]:
    PlaceholderPanel,
};

const DeliveryBarSidePanel = () => {
  const { isSidePanelOpen, setIsSidePanelOpen } = useDeliveryBar();

  const handleClosePanel = () => {
    setIsSidePanelOpen(false);
  };

  const parentValue =
    DeliveryBarMachineContext.useSelector(machineValueSelector);

  const shippingMethodActor = DeliveryBarMachineContext.useSelector(
    (state) => state.children?.shippingMethodSelectorMachine,
  );

  const childValue = useSelector(shippingMethodActor, (state) => {
    if (!state || typeof state !== 'object' || !('value' in state)) {
      return undefined;
    }

    return state.value;
  });

  const machineStateValue = childValue
    ? `${parentValue}.${childValue}`
    : parentValue;

  const accessor = isString(machineStateValue)
    ? machineStateValue
    : Object.keys(machineStateValue)[0];

  const DisplayedPanel: Panel | null = get(panels, accessor);

  if (!DisplayedPanel) return null;

  return (
    <SidePanel.SidePanel isOpen={isSidePanelOpen} onClose={handleClosePanel}>
      <DisplayedPanel closePanel={handleClosePanel} />
    </SidePanel.SidePanel>
  );
};

const AbTestWrapper = () => {
  const isDeliveryBarABTestEnabled = useFeatureIsOn(ABTEST_DELIVERY_BAR);

  if (!isDeliveryBarABTestEnabled) {
    return null;
  }

  return <DeliveryBarSidePanel />;
};

export { AbTestWrapper as DeliveryBarSidePanel };
